import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import DataTable, { Column } from './DataTable';
import EmptyState from './EmptyState';
import { MetricCard } from './MetricCards';
import LoadErrorBox from './ui/LoadErrorBox';
import StatusBadge from './ui/StatusBadge';
import { idColumn } from './ui/idColumn';
import { useExport } from './header/exporters';
import { useShippingAgents } from '../hooks/useShippingAgents';
import { useHeaderSearch } from '../hooks/useHeaderSearch';
import { useRole } from '../hooks/useRole';
import { useProcurementBar } from '../hooks/useProcurementBar';
import { entityPath } from '../utils/entityRoutes';
import { formatDecimal } from '../utils/decimal';
import { SHIPMENT_MODE_LABELS } from './procurement/procurementLabels';
import type { ShippingAgent } from '../types/Procurement';
import '../styles/components/ProcurementScreens.css';

export const agentMatches = (a: ShippingAgent, term: string): boolean => {
  if (!term) return true;
  return [a.name, a.slug, a.email, a.phone, a.website, a.notes, ...a.offices.map(o => o.label)]
    .some(v => (v ?? '').toLowerCase().includes(term));
};

/** The headline rate of an agent: per kg for air, per CBM otherwise. */
const headlineRate = (a: ShippingAgent): string =>
  a.shipping_mode === 'air' ? formatDecimal(a.rate_per_kg, a.currency) : formatDecimal(a.rate_per_cbm, a.currency);

const ShippingAgentsView: React.FC = () => {
  const navigate = useNavigate();
  const { canEdit, canViewFinancials } = useRole();
  const { agents: bar } = useProcurementBar();
  const search = useHeaderSearch().trim().toLowerCase();
  const { allAgents, loading, error, reload } = useShippingAgents();

  const shown = useMemo(
    () => allAgents.filter(a => (bar.showInactive ? !a.active : a.active) && agentMatches(a, search)),
    [allAgents, bar.showInactive, search],
  );
  useExport('shipping', { rows: shown, ctx: undefined });

  if (error) {
    return <div className="wt-container fade-in"><LoadErrorBox what="shipping agents" error={error} onRetry={reload} retrying={loading} /></div>;
  }

  const active = allAgents.filter(a => a.active).length;
  const columns: Column<ShippingAgent>[] = [
    idColumn<ShippingAgent>('shippingAgent'),
    { key: 'name', header: 'Agent', sortable: true },
    { key: 'shipping_mode', header: 'Mode', sortable: true, render: (_v, a) => SHIPMENT_MODE_LABELS[a.shipping_mode] ?? a.shipping_mode },
    { key: 'offices', header: 'Offices', align: 'right', render: (_v, a) => a.offices.length },
    ...(canViewFinancials ? [
      { key: 'rate_per_cbm', header: 'Rate', align: 'right' as const, render: (_v: unknown, a: ShippingAgent) => `${headlineRate(a)}${a.shipping_mode === 'air' ? ' /kg' : ' /CBM'}` },
      { key: 'rates', header: 'Rate cards', align: 'right' as const, render: (_v: unknown, a: ShippingAgent) => a.rates?.length ?? 0 },
    ] : []),
    { key: 'active', header: 'Status', sortable: true, render: (_v, a) => <StatusBadge variant={a.active ? 'success' : 'neutral'}>{a.active ? 'Active' : 'Inactive'}</StatusBadge> },
  ];

  return (
    <div className="wt-container fade-in">
      <section className="stats-grid">
        <MetricCard label="Agents" value={allAgents.length} tone="pink" icon="truck" />
        <MetricCard label="Active" value={active} tone="green" icon="check" />
        <MetricCard label="Inactive" value={allAgents.length - active} tone="blue" icon="folder" />
      </section>

      {!loading && shown.length === 0 ? (
        <EmptyState
          icon="truck"
          title={allAgents.length === 0 ? 'No shipping agents yet' : bar.showInactive ? 'No inactive agents' : 'No agents match'}
          description={allAgents.length === 0 ? 'Shipping agents move goods between the seller and the project. Their rates and fees are kept here.' : undefined}
          action={canEdit && allAgents.length === 0 ? <button type="button" className="primary-btn" onClick={() => navigate('/procurement/shipping-agents/new')}>New agent</button> : undefined}
        />
      ) : (
        <DataTable<ShippingAgent>
          recordList={{ kind: 'shippingAgent' }}
          data={shown}
          columns={columns}
          loading={loading}
          emptyMessage="No agents"
          onRowClick={a => navigate(entityPath('shippingAgent', a.id) ?? '/procurement/shipping')}
        />
      )}
    </div>
  );
};

export default ShippingAgentsView;
